import { useState, useEffect } from 'react'
import { User } from '@/lib/api'
import DashboardSidebar from './DashboardSidebar'
import { Menu, X } from 'lucide-react'
import { Spin } from 'antd'

interface DashboardLayoutProps {
  children: React.ReactNode
  user: User | null
  onLogout: () => void
  title?: string
  subtitle?: string
  titleIcon?: React.ReactNode
  headerActions?: React.ReactNode
}

export default function DashboardLayout({
  children,
  user,
  onLogout,
  title,
  subtitle,
  titleIcon,
  headerActions
}: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Handle responsive detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false)
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sidebarOpen && isMobile) {
        const sidebar = document.getElementById('mobile-sidebar')
        const menuButton = document.getElementById('menu-button')
        if (sidebar && !sidebar.contains(e.target as Node) && menuButton && !menuButton.contains(e.target as Node)) {
          setSidebarOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [sidebarOpen, isMobile])

  // Close sidebar on route change (mobile)
  const handleNavigation = () => {
    if (isMobile) {
      setSidebarOpen(false)
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-[#0B0F19]">
      {/* Mobile Overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: Fixed, Mobile: Drawer */}
      <aside
        id="mobile-sidebar"
        className={`
          fixed h-screen z-50 transition-transform duration-300 ease-in-out
          w-72
          lg:translate-x-0 lg:z-30
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <DashboardSidebar
          user={user}
          onLogout={onLogout}
          onNavigate={handleNavigation}
        />

        {/* Close button for mobile */}
        {isMobile && sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white lg:hidden"
          >
            <X size={20} />
          </button>
        )}
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-72 min-h-screen">
        {/* Mobile Header */}
        <header className="sticky top-0 z-30 bg-[#0B0F19]/95 backdrop-blur-sm border-b border-slate-800/50 px-4 py-3 lg:hidden">
          <div className="flex items-center justify-between">
            <button
              id="menu-button"
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white"
            >
              <Menu size={24} />
            </button>

            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center">
                <span className="text-white text-sm font-bold">A</span>
              </div>
              <span className="font-semibold text-white text-sm">Accounting AI</span>
            </div>

            <div className="w-10" /> {/* Spacer for balance */}
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-6">
          {/* Page Header */}
          {title && (
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
                  {titleIcon}
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-slate-400 text-sm mt-1">{subtitle}</p>
                )}
              </div>
              {headerActions && (
                <div className="flex items-center gap-2">
                  {headerActions}
                </div>
              )}
            </div>
          )}

          {children}
        </div>
      </main>
    </div>
  )
}
